/**
 * Shrink a photo in the browser before it is sent (2026-09-24).
 *
 * A phone photo is 3–15 MB, and it reaches the API as base64 inside JSON,
 * which the API holds several copies of while it reads it: one 14 MB PNG cost
 * the staging API about 150 MB and pushed it past its 512 MB limit, which
 * restarted it mid-upload ("Failed to fetch"). Nothing the shop shows is
 * wider than 1,600 pixels, so a photo is sent at most 2,400 on its long side,
 * as WebP — typically a tenth of the size, and a tenth of the memory.
 */

/** The long side a photo is sent at, at most: comfortably above the 1,600px zoom. */
export const PHOTO_MAX_EDGE = 2400;

/** Below this a photo is sent as it is, even if it could be re-encoded smaller. */
const SMALL_ENOUGH_BYTES = 2 * 1024 * 1024;

/** The size to draw a photo at, or null when it already fits. */
export function shrunkSize(
  width: number,
  height: number,
  maxEdge = PHOTO_MAX_EDGE,
): { width: number; height: number } | null {
  const longest = Math.max(width, height);
  if (longest <= maxEdge || longest === 0) return null;
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * The file to upload: the photo shrunk and re-encoded when that makes it
 * smaller, otherwise the file as picked.
 *
 * Never fails. A format the browser cannot draw (HEIC, say) goes up as it is,
 * and the API says in words what it can and cannot use.
 */
export async function shrinkPhoto(file: File): Promise<File> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return file;
  try {
    // Orientation applied now, so the photo as sent is the photo as seen.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const target = shrunkSize(bitmap.width, bitmap.height);
    if (target === null && file.size <= SMALL_ENOUGH_BYTES) {
      bitmap.close();
      return file;
    }
    const size = target ?? { width: bitmap.width, height: bitmap.height };
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d');
    if (context === null) {
      bitmap.close();
      return file;
    }
    context.drawImage(bitmap, 0, 0, size.width, size.height);
    bitmap.close();
    // WebP keeps a PNG's transparency, and the API makes WebP sizes anyway.
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/webp', 0.9),
    );
    if (blob === null || blob.size >= file.size) return file;
    const name = file.name.replace(/\.[^.]+$/, '') + '.webp';
    return new File([blob], name, { type: 'image/webp', lastModified: file.lastModified });
  } catch {
    return file;
  }
}
